import React, {useEffect} from 'react';
import colors from 'theme/Colors';
import {spacing} from 'theme/Units';
import {TextStyle, Text, View, ViewStyle, StyleSheet} from 'react-native';
import {NativeStackScreenProps} from '@react-navigation/native-stack';
import {RootNavigatorParams, Routes} from 'navigation/Routes';
import {useDispatch, useSelector} from 'react-redux';
import {
  employeeFailure,
  employeeSuccess,
} from 'redux/selectors/employeeSelector';
import {useToast} from 'hooks';
import {resetMessage, addEmployeeRequest} from 'redux/slices/employeeSlice';
import {getOrgID} from 'redux/selectors/authSelector';
import {ERROR_KEY, SUCCESS_KEY} from 'constants/keys';
import {
  ErrorCodes,
  getErrorMessage,
  getSuccessMessage,
  SuccessCodes,
} from 'utils/getMessages';
import {getFormattedDate} from 'utils/getFormattedDate';
import {projectList} from 'redux/selectors/projectSelector';
import {Project, getProjectsRequest} from 'redux/slices/projectSlice';
import {EmployeeForm} from 'components/employee-screen/EmployeeForm';
import { Icon } from 'ui-kits';

type initialValuesType = {
  first_name: string;
  last_name: string;
  email: string;
  personal_email: string;
  phone: string;
  dob: Date | null;
  date_of_joining: Date | null;
  project: Project[];
};

let initialValues: initialValuesType = {
  first_name: '',
  last_name: '',
  email: '',
  personal_email: '',
  phone: '',
  dob: null,
  date_of_joining: null,
  project: [],
};

export function AddEmployee({
  navigation,
}: NativeStackScreenProps<RootNavigatorParams>) {
  const {showToasts} = useToast();
  const dispatch = useDispatch();
  const org_id = useSelector(getOrgID);
  const errorAddEmployee = useSelector(employeeFailure);
  const successAddEmployee = useSelector(employeeSuccess);
  const projects = useSelector(projectList);

  // display success message
  useEffect(() => {
    if (successAddEmployee) {
      showToasts(
        SUCCESS_KEY,
        getSuccessMessage(successAddEmployee as SuccessCodes),
        'success',
      );
      dispatch(resetMessage());
      if (successAddEmployee === 'S-10003') {
        navigation.navigate(Routes.VIEWEMPLOYEE);
      }
    }
  }, [successAddEmployee, showToasts, dispatch, navigation]);

  // display error message
  useEffect(() => {
    if (errorAddEmployee) {
      showToasts(
        ERROR_KEY,
        getErrorMessage(errorAddEmployee as ErrorCodes),
        'error',
      );
      dispatch(resetMessage());
    }
  }, [errorAddEmployee, showToasts, dispatch]);

  useEffect(() => {
    // fetch projects if they are not here
    if (projects.length === 0) {
      dispatch(
        getProjectsRequest({
          org_id,
        }),
      );
    }
  }, [dispatch, org_id, projects.length]);

  function handleCancel() {
    navigation.navigate(Routes.VIEWEMPLOYEE);
  }

  const submitHandler = (values: initialValuesType) => {
    const payload = Object.keys(values).reduce((acc: any, item: string) => {
      if (values[item]) {
        acc[item] = values[item];
      }
      return acc;
    }, {});

    payload.project = values.project.map(proj => proj.id);

    dispatch(
      addEmployeeRequest({
        ...payload,
        org_id,
        is_active: true,
        date_of_joining: getFormattedDate(
          payload.date_of_joining,
          'yyyy-mm-dd',
        ),
        dob: getFormattedDate(payload.dob, 'yyyy-mm-dd'),
      }),
    );
  };

  return (
    <View style={styles.container}>
      <View style={styles.formWrapper}>
        <View style={styles.header}>
          <Text style={styles.heading}>Add Employee</Text>
          <Icon size={28} type="back_arrow" onPress={handleCancel} />
        </View>
        <EmployeeForm
          initialValues={initialValues}
          onSubmit={submitHandler}
          onCancel={handleCancel}
          projects={projects}
        />
      </View>
    </View>
  );
}

type styleType = {
  container: ViewStyle;
  formWrapper: ViewStyle;
  heading: TextStyle;
  header: ViewStyle;
};

const styles: styleType = StyleSheet.create({
  container: {
    alignItems: 'center',
    paddingHorizontal: spacing['10'],
    paddingVertical: spacing['5'],
    backgroundColor: colors.palette.white,
    flex: 1,
  },
  formWrapper: {
    maxWidth: 700,
    width: '100%',
    paddingVertical: spacing['5'],
  },
  heading: {
    color: colors.palette.brandBlue,
    fontWeight: 600,
    fontSize: 40,
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
});
